<script setup>
// A waiting player picks who they'd like to team up with, from the others waiting in
// the same competition (JOIN_SETTINGS_PLAN.md D18). Up to three; hints for the admin.
import TlDialog from '~/components/TlDialog.vue';
import UserProfilePic from '~/components/users/UserProfilePic.vue';
import {usePendingPlacements} from '~/composables/usePendingPlacements';

const props = defineProps({
  // CurrentUserPendingPlacementResponse
  placement: {type: Object, required: true},
  entityName: {type: String, default: ''},
});
const open = defineModel({type: Boolean, default: false});
const emit = defineEmits(['saved']);

const MAX = 3;
const {setPreferredPartners, actionPending, actionErrors} = usePendingPlacements();
const {setAlertMessage} = useAlertService();

const picked = ref([]);
watch(open, (v) => {
  if (v) picked.value = (props.placement.preferredPartners ?? []).filter(p => p.stillWaiting).map(p => p.playerId);
}, {immediate: true});

const candidates = computed(() => props.placement.waitingCandidates ?? []);
const toggle = (id) => {
  if (picked.value.includes(id)) picked.value = picked.value.filter(x => x !== id);
  else if (picked.value.length < MAX) picked.value = [...picked.value, id];
};

const save = async () => {
  const res = await setPreferredPartners(props.placement.id, picked.value);
  if (res) {
    setAlertMessage(picked.value.length ? 'Partner preference saved. We\'ve let them know' : 'Partner preference cleared');
    open.value = false;
    emit('saved', res);
  }
};
</script>

<template>
  <tl-dialog v-model="open" title="Who would you like to team up with?" :max-width="460">
    <p class="ppd-intro">
      Pick up to {{ MAX }} of the other players waiting for {{ entityName || 'this competition' }}. It's a hint for the admin
      who places teams. If they pick you too, you'll usually be placed together.
    </p>

    <div v-if="candidates.length" class="ppd-list">
      <button v-for="c in candidates" :key="c.playerId" type="button" class="ppd-item"
              :class="{'ppd-item--on': picked.includes(c.playerId), 'ppd-item--off': !picked.includes(c.playerId) && picked.length >= MAX}"
              @click="toggle(c.playerId)">
        <v-icon size="20" :icon="picked.includes(c.playerId) ? 'mdi-checkbox-marked-circle' : 'mdi-checkbox-blank-circle-outline'"
                :color="picked.includes(c.playerId) ? 'primary' : 'textSubtle'"/>
        <user-profile-pic :user="{imageFileName: c.imageFileName, fullName: c.fullName}" size="small"/>
        <span class="ppd-item-name">{{ c.fullName }}</span>
        <span v-if="c.isMutual" class="tl-chip tl-chip--ok ppd-chip"><v-icon size="12" icon="mdi-heart" class="mr-1"/>Picked you</span>
      </button>
    </div>
    <div v-else class="ppd-empty">
      <v-icon size="22" color="textSubtle">mdi-account-clock-outline</v-icon>
      <div>No one else is waiting yet. You can pick a partner later.</div>
    </div>

    <crud-error-display class="mt-3" :errors="actionErrors"/>

    <template #actions>
      <v-btn variant="text" @click="open = false">Cancel</v-btn>
      <v-btn color="primary" variant="flat" rounded="pill" :loading="actionPending" @click="save">Save</v-btn>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
.ppd-intro {
  font-size: 13px;
  color: rgb(var(--v-theme-charcoal));
  line-height: 1.5;
  margin-bottom: 12px;
}

.ppd-list {
  border: 1px solid rgb(var(--v-theme-borderLight));
  border-radius: 12px;
  overflow: hidden;
}

.ppd-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  text-align: left;
  border-bottom: 1px solid rgb(var(--v-theme-borderLight));

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: rgb(var(--v-theme-light));
  }

  &--on {
    background: rgb(var(--v-theme-brandTint));
  }

  &--off {
    opacity: 0.5;
  }
}

.ppd-item-name {
  flex: 1 1 auto;
  font-size: 13.5px;
  font-weight: 600;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ppd-chip {
  display: inline-flex;
  align-items: center;
  font-size: 10.5px;
  padding: 2px 8px;
}

.ppd-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px;
  text-align: center;
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  background: rgb(var(--v-theme-light));
  border-radius: 12px;
}
</style>
